const reportFields = ['reportUrl', 'textReportUrl', 'imagesReportUrl', 'beforeReportUrl'];

function reportKey(value, imageTab = false) {
  const url = new URL(value, window.location.origin);

  if (url.pathname === '/optix') {
    return `${url.pathname}?product=${url.searchParams.get('product') || ''}&image_tab=${imageTab || url.searchParams.get('image_tab') === '1' ? '1' : '0'}`;
  }

  return url.pathname;
}

export function findDashboardReport(products, value) {
  const key = reportKey(value);

  for (const product of products) {
    for (const [configId, metric] of Object.entries(product.metrics || {})) {
      const field = reportFields.find(field => metric[field]
        && reportKey(metric[field], field === 'imagesReportUrl') === key);

      if (field) {
        return { product, configId, field };
      }
    }
  }

  return null;
}

function reportTarget(product, config, field) {
  const metric = product.metrics?.[config.id];
  const url = metric?.[field];

  if (!url) {
    return null;
  }

  const prefix = field === 'reportUrl' ? '' : field.replace('ReportUrl', '');

  return {
    url,
    name: config.display_name,
    productName: product.product_name,
    id: metric[`${prefix ? `${prefix}Report` : 'report'}Id`] || null,
    uuid: metric[`${prefix ? `${prefix}Report` : 'report'}Uuid`] || null,
    imageTab: field === 'imagesReportUrl',
  };
}

export function buildDashboardReportNavigation(products, configs, value, wrapProducts = false) {
  const current = findDashboardReport(products, value);

  if (!current) {
    return null;
  }

  const configIndex = configs.findIndex(config => String(config.id) === current.configId);

  if (configIndex < 0) {
    return null;
  }

  let nextReport = null;

  for (let offset = 1; offset < configs.length; offset += 1) {
    const config = configs[(configIndex + offset) % configs.length];
    const fields = Number(config.is_pdp) ? ['textReportUrl', 'imagesReportUrl'] : ['reportUrl'];

    nextReport = fields.map(field => reportTarget(current.product, config, field))
      .find(target => target && reportKey(target.url, target.imageTab) !== reportKey(value)) || null;

    if (nextReport) {
      break;
    }
  }

  const config = configs[configIndex];
  const seen = new Set();
  const availableProducts = products
    .map(product => reportTarget(product, config, current.field))
    .filter(target => {
      if (!target) {
        return false;
      }

      const key = reportKey(target.url, target.imageTab);

      if (seen.has(key)) {
        return false;
      }

      seen.add(key);
      return true;
    });
  const index = availableProducts.findIndex(target => reportKey(target.url, target.imageTab) === reportKey(value));
  const canWrap = wrapProducts && availableProducts.length > 1;

  return {
    current,
    nextReport,
    previousProduct: availableProducts[index - 1] || (canWrap ? availableProducts[availableProducts.length - 1] : null),
    nextProduct: availableProducts[index + 1] || (canWrap ? availableProducts[0] : null),
  };
}

// Reports share the embedded heading, including templates rendered outside Vue.
// Mount here so navigation uses the Dashboard's existing data and applies to every template.
export function mountDashboardReportNavigation(doc, navigation, navigate) {
  if (!navigation) {
    return () => {};
  }

  const mountedNodes = [];
  const restoreLabels = [];
  const disposeTooltips = [];
  let observer;
  const cleanup = () => {
    observer?.disconnect();
    disposeTooltips.forEach(dispose => dispose());
    mountedNodes.forEach(node => node.remove());
    restoreLabels.forEach(restore => restore());
    doc.querySelector('.dashboard-product-search-toolbar')?.classList.remove('dashboard-product-search-toolbar');
  };
  const mount = () => {
    const standalone = !doc.documentElement.classList.contains('in-iframe');
    const heading = (standalone && (
      doc.querySelector('.header-block .title-edit-container h2')
      || doc.querySelector('.header-block .client-name-container h2')
    ))
      || doc.querySelector('.header-block-iframe h1, .cv-heading h1, .pdp-analysis-wrap h5');

    if (!heading) {
      return false;
    }

    const title = heading.parentElement;
    const { nextReport, previousProduct, nextProduct } = navigation;
    const commerce = Boolean(heading.closest('.cv-heading'));
    const search = commerce ? doc.querySelector('.cv-toolbar .cv-search') : null;

    // Commerce renders its search toolbar asynchronously. Wait for the actual destination.
    if (commerce && (previousProduct || nextProduct) && !search && !doc.querySelector('.cv-guest')) {
      return false;
    }

    if (nextReport) {
      // PDP Analysis uses its product name as h1 and a separate report-type label above it.
      const label = title.closest('.embedded-report-title, .cv-heading') ? heading : title.firstElementChild;
      const button = doc.createElement('button');

      button.type = 'button';
      button.className = 'dashboard-next-report';
      button.textContent = label.textContent.trim();
      button.title = `Switch to ${nextReport.name}`;
      button.addEventListener('click', () => navigate(nextReport));
      const originalText = label.textContent;

      restoreLabels.push(() => { label.textContent = originalText; });
      label.textContent = '';
      label.appendChild(button);

      const jquery = doc.defaultView.jQuery || doc.defaultView.$;

      if (typeof jquery?.fn?.tooltip === 'function') {
        const tooltip = jquery(button);

        tooltip.tooltip({
          container: doc.body,
          placement: 'bottom',
          trigger: 'hover focus',
          html: false,
          delay: {
            show: 120,
            hide: 50,
          },
        });
        disposeTooltips.push(() => tooltip.tooltip('dispose'));
      }
    }

    if ((previousProduct || nextProduct) && (!commerce || search)) {
      const arrows = doc.createElement('nav');

      arrows.className = commerce
        ? 'dashboard-product-navigation dashboard-product-navigation--search'
        : 'dashboard-product-navigation';
      arrows.setAttribute('aria-label', 'Product navigation');
      mountedNodes.push(arrows);

      const controls = [
        [previousProduct, 'Previous product', '‹'],
        [nextProduct, 'Next product', '›'],
      ];

      for (const [target, label, symbol] of controls) {
        const button = doc.createElement('button');

        button.type = 'button';
        button.textContent = symbol;
        button.disabled = !target;
        button.title = commerce || !target?.productName ? label : `${label}: ${target.productName}`;
        button.setAttribute('aria-label', label);

        if (commerce) {
          const svg = doc.createElementNS('http://www.w3.org/2000/svg', 'svg');
          const path = doc.createElementNS('http://www.w3.org/2000/svg', 'path');

          svg.setAttribute('viewBox', '0 0 24 24');
          svg.setAttribute('width', '20');
          svg.setAttribute('height', '20');
          svg.setAttribute('fill', 'none');
          svg.setAttribute('stroke', 'currentColor');
          svg.setAttribute('stroke-width', '2');
          svg.setAttribute('stroke-linecap', 'round');
          svg.setAttribute('stroke-linejoin', 'round');
          svg.setAttribute('aria-hidden', 'true');
          path.setAttribute('d', label === 'Previous product' ? 'm15 18-6-6 6-6' : 'm9 18 6-6-6-6');
          svg.appendChild(path);
          button.textContent = '';
          button.appendChild(svg);
        }
        button.addEventListener('click', () => target && navigate(target));
        arrows.appendChild(button);
      }

      if (commerce) {
        search.after(arrows);
        search.parentElement.classList.add('dashboard-product-search-toolbar');
      } else {
        title.after(arrows);
      }
    }

    // The old PDP-only dropdown is redundant inside the Dashboard.
    doc.querySelector('.pdp-url-row--nav')?.classList.remove('pdp-url-row--nav');

    const style = doc.createElement('style');

    style.textContent = `
      .dashboard-next-report {
        font: inherit;
        color: inherit;
        text-align: inherit;
        letter-spacing: inherit;
        text-transform: inherit;
        padding: 0;
        border: 0;
        background: transparent;
        cursor: pointer;
      }
      .dashboard-next-report:hover,
      .dashboard-next-report:focus-visible {
        text-decoration: underline;
        text-decoration-color: #b8c6c2;
        text-underline-offset: 5px;
      }
      .dashboard-product-navigation {
        display: inline-flex;
        align-items: center;
        gap: 4px;
        margin-left: auto;
      }
      .dashboard-product-navigation button {
        width: 28px;
        height: 28px;
        border: 0;
        border-radius: 5px;
        background: transparent;
        color: #526475;
        font-size: 24px;
        line-height: 1;
        cursor: pointer;
      }
      .dashboard-product-navigation button:hover:not(:disabled) {
        background: #dceee5;
      }
      .dashboard-product-navigation button:disabled {
        opacity: .3;
        cursor: default;
      }
      .dashboard-product-navigation--search {
        flex: 0 0 80px;
        margin-left: 0;
      }
      .dashboard-product-navigation--search button {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        width: 38px;
        height: 38px;
        border: 1px solid var(--cv-border, #e2e8f0);
        border-radius: 5px;
        background: #f8fafc;
        color: #0f172a;
      }
      .dashboard-product-navigation--search button:hover:not(:disabled) {
        background: #f1f5f9;
      }
      .dashboard-product-navigation--search button:focus-visible {
        outline: 2px solid #0f172a;
        outline-offset: 2px;
      }
      @media (max-width: 850px) {
        .dashboard-product-search-toolbar .cv-search {
          flex-basis: calc(100% - 89px);
          min-width: 0;
        }
      }
    `;
    doc.head.appendChild(style);
    mountedNodes.push(style);

    return true;
  };

  if (mount()) {
    return cleanup;
  }

  observer = new doc.defaultView.MutationObserver(() => {
    if (mount()) {
      observer.disconnect();
    }
  });

  observer.observe(doc.body, { childList: true, subtree: true });

  return cleanup;
}
